import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { Stack, Typography } from '@mui/material';
import { ScreenComponent, ScreenConfig } from 'new/routes/RouterManager';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { BannerDescription } from 'new/elements/BannerDescription';
import { deleteOrgCustodian, fetchOrgCustodians, fetchOrgMembers } from 'js/actions';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { ADD_ORG_CUSTODIAN_REQUEST, GET_ORG_CUSTODIANS_REQUEST } from 'js/constants/actionTypes';
import { useOrgCustodians } from 'new/hooks/adapters/useOrgCustodians';
import NoContentTextView from 'js/components/elements/NoContentTextView';
import { Table } from 'new/elements/Table';
import { useModals } from 'new/hooks/useModals';
import { ModalAddOrgCustodian } from './ModalAddOrgCustodian';
import { columns } from './columns';
import styles from './DataCustodians.module.scss';
import { useDistance } from 'new/hooks/useDistance';
import { showAlert } from 'new/stores/showAlert/action';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { ReactComponent as PackageIcon } from 'new/components/icons/fi-rr-package.svg';
import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-rr-trash.svg';
import Menu from 'js/components/elements/menu/Menu';

interface DataCustodiansProps {}

const description =
  'Custodians ensure proper data governance and access, ensuring compliance with rules and policies. They manage data access for the organization.';

export const DataCustodians: ScreenComponent<DataCustodiansProps> = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const { openModal } = useModals();
  const containerRef = useRef();
  const distance = useDistance(containerRef, 400);
  const { id: orgId } = useCurrentOrg();
  const user = useCurrentUser();
  const isCustodianOrAdmin = user.isOrgCustodian || user.isAdmin;
  const isLoading = useIsLoading(GET_ORG_CUSTODIANS_REQUEST, ADD_ORG_CUSTODIAN_REQUEST);
  const currentOrgCustodians = useOrgCustodians();

  const handleRemoveCustodian = (data) => {
    dispatch(deleteOrgCustodian(orgId, data?.id)).then(() => {
      dispatch(
        showAlert({
          title: `Data Custodian "${data.full_name}" deleted`,
          severity: 'success',
        }),
      );
    });
  };

  useEffect(() => {
    if (orgId) {
      dispatch(fetchOrgCustodians(orgId));
      dispatch(fetchOrgMembers(orgId));
    }
  }, [orgId]);

  const onClickAddOrgCustodian = () => {
    openModal(ModalAddOrgCustodian.modalConfig.name);
  };

  const getRowClass = (row) => {
    const classname = styles.rowWrapper;
    return classname;
  };

  const rowEndRender = (row) => {
    if (!row) {
      return null;
    }
    return (
      <Menu
        items={[
          {
            type: 'dialog',
            label: 'Remove Custodian',
            icon: <DeleteIcon />,
            color: 'error',
            dialogMessage: `Please confirm you want to delete Org Custodian: ${row.full_name}`,
            discardLabel: 'Cancel',
            confirmLabel: 'Delete',
            onClick: () => handleRemoveCustodian(row),
          },
        ]}
      />
    );
  };

  return (
    <>
      <BannerDescription
        title="Data Custodian"
        description={description}
        onClick={isCustodianOrAdmin ? onClickAddOrgCustodian : null}
        actionTitle={isCustodianOrAdmin ? 'Add Org Custodian' : null}
      />
      {!isLoading && !isCustodianOrAdmin && (
        <Stack direction="row" className={styles.noAccess}>
          <PackageIcon className={styles.noAccessIcon} />
          <div className={styles.noAccessText}>
            <Typography variant="h3">
              Org Custodian management is only accessible to Org Custodians or System
              Administrators.
            </Typography>
            <Typography variant="body1">
              Contact a System Administrator for Org Custodian permissions.
            </Typography>
          </div>
        </Stack>
      )}
      {!isLoading && !currentOrgCustodians && (
        <NoContentTextView
          title={<div>No Organization Custodians. Click on the button above to get started.</div>}
        />
      )}
      {isCustodianOrAdmin ? (
        <Table
          height={distance}
          getRowClassName={getRowClass}
          isSkeletonLoadingMode
          loading={isLoading}
          parentScrollRef={containerRef}
          columns={columns}
          rows={Object.values(currentOrgCustodians)}
          rowEndRender={rowEndRender}
        />
      ) : null}
    </>
  );
};

DataCustodians.screenConfig = {
  path: `${ROUTE_REDESIGN}/settings/data-custodians`,
  domain: 'settings',
  modals: [ModalAddOrgCustodian],
  header: {
    title: 'Data Custodians',
    slugTitle: 'Data Custodians',
  },
  screenName: 'DataDomains',
} as ScreenConfig<DataCustodiansProps>;
